/**
 * DragMatchQuiz — drag a name tag onto the correct person in the image.
 *
 * Data contract:
 *   quiz.question.question_text          → name to drag (e.g. "Mary")
 *   quiz.context.context_image_url       → image URL
 *   quiz.context.persons                 → [{id, x, y, w, h, label}] (label = option id)
 *   quiz.answer_feedback.correct_option_id       → "C"
 *   quiz.answer_feedback.teacher_feedback_correct
 *   quiz.answer_feedback.teacher_feedback_incorrect
 */
import React, { useRef, useState } from 'react';

const COLORS = ['#ef4444','#3b82f6','#22c55e','#f59e0b','#a855f7','#ec4899','#06b6d4','#f97316'];

/* Inject styles once */
let _injected = false;
function injectStyles() {
  if (_injected || document.getElementById('drag-match-styles')) return;
  _injected = true;
  const s = document.createElement('style');
  s.id = 'drag-match-styles';
  s.textContent = `
    @keyframes dmPop   { 0%{opacity:0;transform:scale(.5)} 70%{transform:scale(1.1)} 100%{opacity:1;transform:scale(1)} }
    @keyframes dmShake { 0%,100%{transform:translateX(0)} 25%,75%{transform:translateX(-6px)} 50%{transform:translateX(6px)} }
    @keyframes dmFadeUp{ from{opacity:0;transform:translateY(12px)} to{opacity:1;transform:translateY(0)} }
    .dm-pop   { animation: dmPop .4s cubic-bezier(.34,1.56,.64,1) both }
    .dm-shake { animation: dmShake .4s ease both }
    .dm-fadeup{ animation: dmFadeUp .3s ease both }
    .dm-drop-zone { transition: background .15s, border-color .15s, transform .15s; }
    .dm-drop-zone.dm-over { background: rgba(99,102,241,.18) !important; border-color: #6366f1 !important; transform: scale(1.04); }
    .dm-name-tag { cursor: grab; transition: box-shadow .15s, transform .15s; }
    .dm-name-tag:active { cursor: grabbing; transform: scale(1.06); box-shadow: 0 8px 24px rgba(0,0,0,.18); }
  `;
  document.head.appendChild(s);
}

export default function DragMatchQuiz({
  quiz,
  mediaBase = '',
  onAnswer,
  onSubmitWithAnswer,
  hideRetry = false,
  hideInlineFeedback = false,
}) {
  injectStyles();

  const persons   = quiz.context?.persons || [];
  const imageUrl  = quiz.context?.context_image_url || '';
  const name      = quiz.question?.question_text || '?';
  const feedback  = quiz.answer_feedback || {};
  const correctId = feedback.correct_option_id;

  const [result, setResult] = useState(null); // null | 'correct' | 'wrong'
  const [droppedOn, setDroppedOn] = useState(null); // person id dropped on
  const [overZone, setOverZone] = useState(null);
  const imgRef = useRef(null);

  /* ── Drag handlers for the name tag ── */
  const onDragStart = (e) => {
    e.dataTransfer.setData('text/plain', 'name');
    e.dataTransfer.effectAllowed = 'move';
  };

  /* ── Drop zone handlers ── */
  const onDragOver = (e, personId) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setOverZone(personId);
  };
  const onDragLeave = () => setOverZone(null);

  const onDrop = (e, person) => {
    e.preventDefault();
    setOverZone(null);
    if (result) return; // already answered

    const isCorrect = person.label === correctId;
    setDroppedOn(person.id);
    setResult(isCorrect ? 'correct' : 'wrong');
    const label = person.label != null ? String(person.label) : '';
    if (onSubmitWithAnswer) onSubmitWithAnswer({ userAnswer: label, isCorrect });
    else if (onAnswer) onAnswer(isCorrect);
  };

  const reset = () => { setResult(null); setDroppedOn(null); setOverZone(null); };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

      {/* ── Instruction ── */}
      <p style={{ margin: 0, fontSize: 13, color: '#64748b', fontWeight: 600 }}>
        🎧 Nghe và kéo tên <strong style={{ color: '#6366f1' }}>{name}</strong> vào đúng người trong hình.
      </p>

      {/* ── Name tag (draggable) ── */}
      {!result && (
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <div
            className="dm-name-tag"
            draggable
            onDragStart={onDragStart}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 8,
              padding: '10px 24px', borderRadius: 999,
              background: 'linear-gradient(135deg,#6366f1,#8b5cf6)',
              color: 'white', fontWeight: 800, fontSize: 16,
              boxShadow: '0 4px 14px rgba(99,102,241,.35)',
              userSelect: 'none',
            }}
          >
            <span style={{ fontSize: 18 }}>🏷️</span>
            {name}
          </div>
        </div>
      )}

      {/* ── Image with drop zones ── */}
      <div style={{ position: 'relative', width: '100%' }}>
        <img
          ref={imgRef}
          src={`${mediaBase}${imageUrl}`}
          alt="context"
          draggable={false}
          style={{ width: '100%', borderRadius: 12, display: 'block', userSelect: 'none' }}
        />

        {persons.map((person, i) => {
          const color = COLORS[i % COLORS.length];
          const isOver = overZone === person.id;
          const isDropped = droppedOn === person.id;
          const isCorrectZone = person.label === correctId;

          let borderColor = color;
          let bgColor = `${color}22`;
          if (result && isDropped) {
            borderColor = result === 'correct' ? '#22c55e' : '#ef4444';
            bgColor = result === 'correct' ? 'rgba(34,197,94,.2)' : 'rgba(239,68,68,.2)';
          } else if (result && isCorrectZone) {
            borderColor = '#22c55e';
            bgColor = 'rgba(34,197,94,.15)';
          }

          return (
            <div
              key={person.id}
              className={`dm-drop-zone${isOver && !result ? ' dm-over' : ''}`}
              onDragOver={(e) => !result && onDragOver(e, person.id)}
              onDragLeave={onDragLeave}
              onDrop={(e) => onDrop(e, person)}
              style={{
                position: 'absolute',
                left: `${person.x}%`, top: `${person.y}%`,
                width: `${person.w}%`, height: `${person.h}%`,
                border: `2.5px ${result ? 'solid' : 'dashed'} ${borderColor}`,
                borderRadius: 8,
                background: bgColor,
                boxSizing: 'border-box',
                cursor: result ? 'default' : 'copy',
              }}
            >
              {/* Number badge */}
              <div style={{
                position: 'absolute', top: -1, left: -1,
                background: borderColor, color: 'white',
                fontSize: 10, fontWeight: 800,
                padding: '1px 5px', borderRadius: '0 0 6px 0',
                lineHeight: 1.5,
              }}>
                {person.id}
              </div>

              {/* Show dropped name or correct indicator */}
              {isDropped && result && (
                <div className="dm-pop" style={{
                  position: 'absolute', inset: 0,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  flexDirection: 'column', gap: 2,
                }}>
                  <span style={{ fontSize: 20 }}>{result === 'correct' ? '✅' : '❌'}</span>
                  <span style={{ fontSize: 10, fontWeight: 800, color: borderColor }}>{name}</span>
                </div>
              )}

              {/* Show correct zone indicator after wrong answer */}
              {result === 'wrong' && isCorrectZone && !isDropped && (
                <div className="dm-pop" style={{
                  position: 'absolute', inset: 0,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <span style={{ fontSize: 18 }}>✅</span>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* ── Feedback ── */}
      {result && !hideInlineFeedback && (
        <div
          className="dm-fadeup"
          style={{
            padding: '16px 20px', borderRadius: 14,
            background: result === 'correct' ? '#f0fdf4' : '#fef2f2',
            border: `1.5px solid ${result === 'correct' ? '#86efac' : '#fca5a5'}`,
          }}
        >
          <p style={{
            margin: '0 0 10px', fontSize: 15, fontWeight: 800,
            color: result === 'correct' ? '#166534' : '#991b1b',
          }}>
            {result === 'correct' ? '🎉 Chính xác!' : '❌ Chưa đúng!'}
          </p>
          <p style={{
            margin: 0, fontSize: 13, lineHeight: 1.7, whiteSpace: 'pre-wrap',
            color: result === 'correct' ? '#15803d' : '#b91c1c',
          }}>
            {result === 'correct'
              ? feedback.teacher_feedback_correct
              : feedback.teacher_feedback_incorrect}
          </p>
          {!hideRetry && (
            <button
              onClick={reset}
              style={{
                marginTop: 12, padding: '7px 18px', borderRadius: 10,
                border: 'none', cursor: 'pointer', fontWeight: 700, fontSize: 13,
                background: result === 'correct' ? '#22c55e' : '#6366f1',
                color: 'white',
              }}
            >
              Thử lại
            </button>
          )}
        </div>
      )}
    </div>
  );
}
